import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { Link, useParams } from 'react-router-dom';
import { allComics } from '@/mocks/comics';
import { reviews } from '@/mocks/reviews';

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comic = allComics.find((c) => c.id === id);
  const relatedComics = allComics.filter((c) => c.id !== id).slice(0, 4);

  if (!comic) {
    return (
      <>
        <SEOHead title="漫画未找到 - 基德漫画网" description="抱歉，未找到该漫画" />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-3">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">请检查链接是否正确</p>
            <Link to="/" className="px-6 py-2 bg-primary-500 text-white rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 韩漫免费漫画在线观看下拉式 - 基德漫画网`}
        description={`${comic.description} 基德漫画网提供${comic.title}韩漫免费漫画在线观看下拉式阅读，作者${comic.author}，评分${comic.rating}，${(comic.views / 10000).toFixed(0)}万次阅读。`}
        keywords={`${comic.title},${comic.category}漫画,韩漫免费漫画,在线观看下拉式,基德漫画网,${comic.author}`}
        canonicalUrl={`http://bjgjfh.com/comic/${comic.id}`}
        ogType="article"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - ${comic.author}`,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: '2026-01-15',
            dateModified: '2026-07-01',
          },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: `${comic.category}漫画`, path: `/category/${comic.category === '恋爱' ? 'romance' : comic.category === '奇幻' ? 'fantasy' : comic.category === '动作' ? 'action' : comic.category === '悬疑' ? 'suspense' : comic.category === '搞笑' ? 'comedy' : 'healing'}` },
                { label: comic.title },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-shrink-0 w-full lg:w-[320px]">
                <div className="rounded-lg overflow-hidden" data-product-shop="">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩漫免费漫画在线观看下拉式`}
                    title={`${comic.title} - 好看韩漫漫画免费漫画`}
                    className="w-full aspect-[3/4] object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <p className="text-xs text-foreground-500 mb-2 flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-accent-100 text-accent-800 rounded text-xs whitespace-nowrap">
                    {comic.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                    </div>
                    {comic.rating}
                  </span>
                </p>

                <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-3 leading-tight">
                  {comic.title}
                </h1>

                <p className="text-sm text-foreground-600 mb-2">作者：{comic.author}</p>

                <p className="text-sm text-foreground-500 leading-relaxed mb-6 max-w-2xl">
                  {comic.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-6 mb-8 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </div>
                    {(comic.views / 10000).toFixed(0)}万 阅读
                  </span>
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-heart-line"></i>
                    </div>
                    收藏
                  </span>
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-chat-1-line"></i>
                    </div>
                    评论
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors text-center whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-book-open-line text-sm"></i>
                    </div>
                    开始下拉式阅读
                  </Link>
                  <button className="px-8 py-3 border border-foreground-300 text-foreground-700 rounded-full text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-bookmark-line text-sm"></i>
                    </div>
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-xl md:text-3xl text-foreground-950 mb-8">
              读者评价
            </h2>

            <div className="flex flex-col gap-6">
              {reviews.map((review) => (
                <article key={review.id} className="bg-background-100 rounded-lg p-6 text-left">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={review.avatar}
                      alt={review.userName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{review.userName}</p>
                      <p className="text-xs text-foreground-500">{review.date}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed italic">
                    "{review.content}"
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-3xl text-foreground-950 mb-8 text-center">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                {relatedComics.map((c, index) => (
                  <ComicCard
                    key={c.id}
                    id={c.id}
                    title={c.title}
                    author={c.author}
                    coverImage={c.coverImage}
                    category={c.category}
                    rating={c.rating}
                    views={c.views}
                    tags={c.tags}
                    isFirst={index === 0}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20reading%20experience%20illustration%20with%20warm%20cozy%20evening%20lighting%2C%20minimalist%20composition%2C%20digital%20reading%20device%20with%20soft%20screen%20glow%2C%20warm%20coral%20and%20amber%20tones%2C%20comfortable%20atmosphere%2C%20Korean%20webtoon%20aesthetic%20background%2C%20inviting%20mood&width=1920&height=700&seq=cta-comic-read&orientation=landscape"
            alt="韩漫免费漫画在线观看下拉式"
            title="韩漫免费漫画在线观看下拉式 - 基德漫画网"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <h2 className="font-heading text-xl md:text-3xl text-white mb-3">
                立即体验<strong className="font-heading text-accent-400">下拉式阅读</strong>
              </h2>
              <p className="text-sm text-white/70 mb-6">流畅连续阅读，尽享韩漫魅力</p>
              <Link
                to={`/comic/${comic.id}`}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                开始阅读
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}